import { useEffect, useState } from "react";
import { api } from "../api";
import {
  KEY_FACT_EMOJI,
  type CaseVolume,
  type KeyFact,
  type ValidationResult,
} from "../types";
import CopyButton from "./ui/CopyButton";
import ValidationBadge from "./ui/ValidationBadge";
import FindingsSummaryBar from "./ui/FindingsSummaryBar";
import { useFindingActions } from "../lib/useFindingActions";
import { useValidationPoll } from "../lib/useValidationPoll";

// Renders a closed volume's cached summary prose + key facts. Used
// inside the Timeline volume-header expander and the Settings volume
// management panel. Lazy-fetches the summary payload on mount so the
// list/header rendering stays light.

interface Props {
  caseId: string;
  volume: CaseVolume;
  onAskAi?: (volumeId: string) => void;
}

export default function VolumeSummary({ caseId, volume, onAskAi }: Props) {
  const [summary, setSummary] = useState<string | null>(null);
  const [keyFacts, setKeyFacts] = useState<KeyFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Separate from `loading` — we keep the previous summary visible
  // while the regen is in flight so the user has continuity rather
  // than a flash of "Loading summary…" state.
  const [regenerating, setRegenerating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const { v: vFindings, onAction: onFindingAction, blocked } =
    useFindingActions(validation, { caseId, target: "volume", volumeId: volume.id });

  // Poll for the background Tier-2 result after a regenerate.
  useValidationPoll(
    validation,
    () => api.volumeSummary(caseId, volume.id).then((r) => r.validation ?? null),
    setValidation,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await api.volumeSummary(caseId, volume.id);
        if (cancelled) return;
        setSummary(res.summary ?? null);
        setKeyFacts(res.key_facts ?? []);
        setValidation(res.validation ?? null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, volume.id]);

  async function regenerate() {
    setRegenerating(true);
    setError(null);
    try {
      const res = await api.regenerateVolumeSummary(caseId, volume.id);
      setSummary(res.summary ?? null);
      setKeyFacts(res.key_facts ?? []);
      setValidation(res.validation ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRegenerating(false);
    }
  }

  const title = volume.title || `Volume ${volume.volume_number}`;

  return (
    <section className="volume-summary">
      <header className="volume-summary-head">
        <h4 className="volume-summary-title">{title} summary</h4>
        <div className="volume-summary-actions">
          <ValidationBadge
            validation={vFindings}
            verifying={regenerating || !!validation?.tier2_pending}
            onAction={onFindingAction}
          />
          {summary && !blocked && (
            <CopyButton
              text={summary}
              title="Copy volume summary to clipboard"
            />
          )}
          <button
            type="button"
            className="btn-subtle volume-summary-regen"
            onClick={() => void regenerate()}
            disabled={regenerating || loading}
            title="Re-run the AI summary for this volume (1 AI call)"
          >
            {regenerating ? "Regenerating…" : "Regenerate"}
          </button>
          {onAskAi && (
            <button
              type="button"
              className="btn-subtle volume-summary-drill"
              onClick={() => onAskAi(volume.id)}
            >
              Ask AI about this volume ↗
            </button>
          )}
        </div>
      </header>

      {loading && <p className="muted">Loading summary…</p>}
      {error && <div className="form-error">{error}</div>}

      {!loading && !error && (
        <>
          {summary ? (
            <>
              <p className="volume-summary-prose">{summary}</p>
              {vFindings && (
                <FindingsSummaryBar
                  validation={vFindings}
                  onAction={onFindingAction}
                  inlineMappable={false}
                />
              )}
            </>
          ) : (
            <p className="muted">
              This volume was closed without a generated summary. Regenerate
              from Settings → Volumes.
            </p>
          )}

          {keyFacts.length > 0 && (
            <ul className="volume-summary-facts">
              {keyFacts.map((f, i) => (
                <li
                  key={`${f.type}-${i}`}
                  className={`volume-summary-fact kf-${f.type}`}
                >
                  <span className="kf-icon" aria-hidden>
                    {KEY_FACT_EMOJI[f.type]}
                  </span>
                  <span className="kf-type">{f.type}</span>
                  <span className="kf-text">{f.fact}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
